// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl, isSignatureItem } from '@intakeform/fhir';
import { AddressField, isAddressItem } from './fields/AddressField.js';
import { DateField } from './fields/DateField.js';
import { FileUpload } from './fields/FileUpload.js';
import { LongText } from './fields/LongText.js';
import { MultiSelect } from './fields/MultiSelect.js';
import { NumberField } from './fields/NumberField.js';
import { Phone } from './fields/Phone.js';
import { ShortText } from './fields/ShortText.js';
import { Signature } from './fields/Signature.js';
import { SingleSelect } from './fields/SingleSelect.js';
import { YesNo } from './fields/YesNo.js';
import { FieldTypeRegistry } from './registry.js';
/** Built-in field id constants. Useful for `registerBefore` calls. */
export const BuiltinFieldId = {
    shortText: 'shortText',
    longText: 'longText',
    singleSelect: 'singleSelect',
    multiSelect: 'multiSelect',
    number: 'number',
    date: 'date',
    yesNo: 'yesNo',
    signature: 'signature',
    fileUpload: 'fileUpload',
    address: 'address',
    phone: 'phone',
};
function hasSignatureExtension(item) {
    return (item.type === 'attachment' &&
        item.extension?.some((e) => e.url === ExtensionUrl.signature && e.valueBoolean === true) ===
            true);
}
function isPhoneItem(item) {
    return (item.type === 'string' &&
        item.extension?.some((e) => e.url === ExtensionUrl.phoneValidation && e.valueBoolean === true) === true);
}
/**
 * Build a fresh registry containing the nine built-in field types in the
 * declaration order documented in `docs/fhir-mapping.md`. Custom types
 * that share an `item.type` should be inserted via `registerBefore`
 * against the relevant builtin id (e.g. before `shortText` for phone).
 */
export function createDefaultRegistry() {
    const r = new FieldTypeRegistry();
    // Signature dispatches before generic file upload because both share
    // `item.type === 'attachment'` and the signature extension is the
    // discriminator.
    r.register({
        id: BuiltinFieldId.signature,
        matches: (item) => isSignatureItem(item) || hasSignatureExtension(item),
        component: Signature,
    });
    r.register({
        id: BuiltinFieldId.fileUpload,
        matches: (item) => item.type === 'attachment',
        component: FileUpload,
    });
    // Address and Phone both dispatch before generic short text — they
    // share `item.type === 'string'` and the opt-in extension is the
    // discriminator (in-7r5 / in-apm). Order between the two doesn't
    // matter because their `matches` predicates are mutually exclusive.
    r.register({
        id: BuiltinFieldId.address,
        matches: (item) => isAddressItem(item),
        component: AddressField,
    });
    r.register({
        id: BuiltinFieldId.phone,
        matches: isPhoneItem,
        component: Phone,
    });
    r.register({
        id: BuiltinFieldId.shortText,
        matches: (item) => item.type === 'string',
        component: ShortText,
    });
    r.register({
        id: BuiltinFieldId.longText,
        matches: (item) => item.type === 'text',
        component: LongText,
    });
    r.register({
        id: BuiltinFieldId.multiSelect,
        matches: (item) => item.type === 'choice' && item.repeats === true,
        component: MultiSelect,
    });
    r.register({
        id: BuiltinFieldId.singleSelect,
        matches: (item) => item.type === 'choice',
        component: SingleSelect,
    });
    r.register({
        id: BuiltinFieldId.number,
        matches: (item) => item.type === 'decimal' || item.type === 'integer',
        component: NumberField,
    });
    r.register({
        id: BuiltinFieldId.date,
        matches: (item) => item.type === 'date',
        component: DateField,
    });
    r.register({
        id: BuiltinFieldId.yesNo,
        matches: (item) => item.type === 'boolean',
        component: YesNo,
    });
    return r;
}
//# sourceMappingURL=builtin.js.map